import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Brand } from "../Brand";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import {
  LayoutGrid, Users, RefreshCw, ShieldAlert, ScrollText, LogOut, ExternalLink, Handshake,
  Wallet, TrendingUp, Banknote, GitBranch, Bell, Receipt, BarChart3, ShieldCheck, Plug,
  UserPlus, Sparkles,
  Menu as MenuIcon, X as XIcon,
} from "lucide-react";

const NAV = [
  { to: "/admin/orders",          label: "Orders",            icon: LayoutGrid },
  { to: "/admin/buy-orders",      label: "Buy Crypto",        icon: Banknote },
  { to: "/admin/customers",       label: "Customers",         icon: Users },
  { to: "/admin/kycaid",          label: "KYCAID queue",      icon: ShieldCheck },
  { to: "/admin/partners",        label: "Partners",          icon: Handshake },
  { to: "/admin/xpay-merchants",  label: "Xchangely Pay",     icon: Sparkles },
  { to: "/admin/xpay-transactions", label: "XPay transactions", icon: Receipt },
  { to: "/admin/xpay-finance",   label: "XPay finance",      icon: TrendingUp },
  { to: "/admin/xpay-webhook-logs", label: "XPay webhooks",     icon: Bell },
  { to: "/admin/xpay-reconcile",  label: "XPay reconcile",    icon: RefreshCw },
  { to: "/admin/wallets",         label: "Wallet approvals",  icon: Wallet },
  { to: "/admin/treasury",        label: "Treasury",          icon: Banknote },
  { to: "/admin/rate-model",      label: "Rate model",        icon: TrendingUp },
  { to: "/admin/pipeline",        label: "Pipeline",          icon: GitBranch },
  { to: "/admin/reconciliation",  label: "Reconciliation",    icon: RefreshCw },
  { to: "/admin/refunds",         label: "Refunds",           icon: Receipt },
  { to: "/admin/reports",         label: "Reports",           icon: BarChart3 },
  { to: "/admin/integrations",    label: "Integrations",      icon: Plug },
  { to: "/admin/team",            label: "Team",              icon: UserPlus },
  { to: "/admin/review",          label: "Manual Review",     icon: ShieldAlert },
  { to: "/admin/audit",           label: "Audit Log",         icon: ScrollText },
];

function AlertBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const tick = () => {
      api.get("/admin/alerts?status=open")
        .then((r) => { if (alive) setCount(r.data.open_count || 0); })
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <button
      onClick={() => navigate("/admin/alerts")}
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl border border-border bg-white hover:bg-muted/40 transition-colors"
      data-testid="admin-alert-bell"
      aria-label={`${count} open alerts`}
    >
      <Bell className="w-4 h-4 text-foreground" />
      {count > 0 && (
        <Badge
          data-testid="admin-alert-count"
          className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] rounded-full p-0 px-1 bg-rose-600 hover:bg-rose-600 text-white text-[10px] font-bold tabular-nums flex items-center justify-center border-2 border-white"
        >
          {count > 99 ? "99+" : count}
        </Badge>
      )}
    </button>
  );
}

export default function AdminLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#0a0e27] text-white flex items-center justify-between px-3 py-2.5 shadow"
           data-testid="admin-mobile-topbar">
        <button onClick={() => navigate("/")} className="px-1">
          <Brand size={20} theme="dark" />
        </button>
        <div className="flex items-center gap-1.5">
          <AlertBell />
          <button onClick={() => setMobileOpen(true)}
                  className="w-9 h-9 rounded-xl hover:bg-white/10 flex items-center justify-center"
                  data-testid="admin-mobile-menu-btn"
                  aria-label="Open menu">
            <MenuIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Backdrop */}
      {mobileOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-30 bg-black/50"
          data-testid="admin-mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside className={`xch-ink w-60 shrink-0 flex flex-col p-4 fixed md:sticky top-0 h-screen z-40 transition-transform duration-200 ${
        mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      }`} data-testid="admin-sidebar">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/")} className="px-1">
            <Brand size={26} theme="dark" />
          </button>
          <button onClick={() => setMobileOpen(false)} className="md:hidden w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center"
                  data-testid="admin-mobile-close-btn"
                  aria-label="Close menu">
            <XIcon className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold px-2 mb-2">
          Back-office
        </div>
        <nav className="space-y-0.5 flex-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-3 space-y-1">
          <div className="px-3 pb-2">
            <div className="text-xs font-semibold text-white/90 truncate">{user?.name}</div>
            <div className="text-[10px] text-white/50 truncate">{user?.email}</div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="admin-back-to-app"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Back to app
          </button>
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 px-4 md:px-8 py-8 pt-16 md:pt-8" data-testid="admin-content">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-3 md:gap-4">
          <div>
            <h1 className="xch-headline text-2xl md:text-3xl">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {actions}
            <div className="hidden md:block">
              <AlertBell />
            </div>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
}

export function AdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) { window.location.replace("/"); return null; }
  if (!user.is_staff) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="admin-403">
        <div className="xch-card p-8 max-w-md text-center space-y-3">
          <div className="font-display text-2xl font-bold text-destructive">403 · Staff only</div>
          <div className="text-sm text-muted-foreground">
            Your account is not authorized for back-office access.
          </div>
          <Button onClick={() => window.location.replace("/")} className="bg-primary hover:bg-primary/90 text-white rounded-xl mt-2">
            Back to widget
          </Button>
        </div>
      </div>
    );
  }
  return children;
}
